import { useEffect, useRef, useState } from "react";
import { C, font } from "../theme";
import { Button } from "../components/Button";
import { useSession } from "../lib/session/SessionContext";
import { fetchInviteValidation, acceptInvite } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";
import { INVITE_STATUS, inviteStatusLabel } from "../lib/enums";
import { hasReachedTenantLimit } from "../lib/tenantLimit";
import { InviteValidationScreen, type InviteValidationState } from "./InviteValidationScreen";
import { SignupScreen } from "./SignupScreen";
import { OnboardingCtScreen } from "./OnboardingCtScreen";
import { OnboardingTeamScreen } from "./OnboardingTeamScreen";
import type { AcceptInviteResult, InviteValidationDto } from "../types";

interface InviteFlowProps {
  code: string;
  onComplete: () => void;
}

type Validation =
  | { kind: "loading" }
  | { kind: "unavailable"; message: string }
  | { kind: "error"; message: string }
  | { kind: "ready"; invite: InviteValidationDto };

type Step =
  | { name: "ready"; invite: InviteValidationDto; kind: "unknown" | "onboarding" }
  | { name: "team"; invite: InviteValidationDto }
  | { name: "limit" }
  | { name: "accept-error"; message: string };

const ONBOARDING_DATA_REQUIRED = "INVITE_ONBOARDING_DATA_REQUIRED";

export function InviteFlow({ code, onComplete }: InviteFlowProps) {
  const { status, accessToken, tenants, tenantsLoaded, ctLimit, refreshTenants } = useSession();
  const [validation, setValidation] = useState<Validation>({ kind: "loading" });
  const [step, setStep] = useState<Step | null>(null);
  const probedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const invite = await fetchInviteValidation(code);
        if (cancelled) return;
        if (invite.status === INVITE_STATUS.Pending) {
          setValidation({ kind: "ready", invite });
          setStep({ name: "ready", invite, kind: "unknown" });
        } else {
          setValidation({ kind: "unavailable", message: `Este convite ${inviteStatusLabel(invite.status).toLowerCase()}.` });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setValidation({ kind: "unavailable", message: "Convite não encontrado. Verifique o link." });
        } else {
          setValidation({ kind: "error", message: err instanceof Error ? err.message : "Não foi possível validar o convite." });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    if (status !== "authed" || !accessToken || !tenantsLoaded || step?.name !== "ready" || step.kind !== "unknown" || probedRef.current) return;
    probedRef.current = true;
    void (async () => {
      try {
        // Convite de team aceita direto (corpo vazio); onboarding responde 400
        // INVITE_ONBOARDING_DATA_REQUIRED e segue para o formulário do CT.
        const result = await acceptInvite(accessToken, code, {});
        await refreshTenants(result.tenantId);
        onComplete();
      } catch (err) {
        if (err instanceof ApiError && err.status === 400 && err.extensions?.code === ONBOARDING_DATA_REQUIRED) {
          if (hasReachedTenantLimit(tenants, ctLimit)) setStep({ name: "limit" });
          else setStep({ name: "ready", invite: step.invite, kind: "onboarding" });
        } else {
          setStep({ name: "accept-error", message: describeAcceptError(err) });
        }
      }
    })();
  }, [status, accessToken, tenantsLoaded, step, tenants, ctLimit, code, refreshTenants, onComplete]);

  const handleAccepted = async (result: AcceptInviteResult) => {
    await refreshTenants(result.tenantId);
    if (step?.name === "ready") setStep({ name: "team", invite: step.invite });
  };

  if (validation.kind === "loading") {
    return <InviteValidationScreen state={{ kind: "loading" }} />;
  }

  if (validation.kind === "unavailable" || validation.kind === "error") {
    return <InviteValidationScreen state={validation} />;
  }

  if (step === null) {
    return <InviteValidationScreen state={{ kind: "loading" }} />;
  }

  if (step.name === "ready" && status === "anon") {
    return <SignupScreen invitedEmail={step.invite.email} />;
  }

  if (step.name === "ready" && step.kind === "unknown") {
    return <InviteValidationScreen state={{ kind: "loading" }} />;
  }

  if (step.name === "ready" && step.kind === "onboarding") {
    return <OnboardingCtScreen code={code} onAccepted={handleAccepted} />;
  }

  if (step.name === "team") {
    return <OnboardingTeamScreen onComplete={onComplete} />;
  }

  if (step.name === "limit") {
    return (
      <InviteValidationScreen state={{ kind: "unavailable", message: `Você atingiu o limite de CTs do seu plano (${ctLimit}). O upgrade estará disponível em breve.` }}>
        <Button onClick={onComplete} style={{ marginTop: 18, fontSize: 13 }}>
          Entrar no painel
        </Button>
      </InviteValidationScreen>
    );
  }

  if (step.name === "accept-error") {
    return (
      <div style={{ minHeight: "100vh", background: C.white, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: font, textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1.2, color: C.ink }}>NÃO FOI POSSÍVEL ACEITAR</div>
        <div style={{ fontSize: 13, color: C.gray, marginTop: 10, maxWidth: 380, lineHeight: 1.6 }}>{step.message}</div>
        <Button onClick={onComplete} style={{ marginTop: 18, fontSize: 13 }}>
          Entrar no painel
        </Button>
      </div>
    );
  }

  return null;
}

function describeAcceptError(err: unknown): string {
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : "Não foi possível aceitar o convite.";
  if (err.status === 409 && err.extensions?.code === "INVITE_NOT_PENDING") return "Este convite já foi usado ou não está mais pendente.";
  if (err.status === 409 && err.extensions?.code === "MEMBER_ALREADY_EXISTS") return "Você já é membro deste CT.";
  if (err.status === 403 && err.extensions?.code === "INVITE_EMAIL_MISMATCH") return "O e-mail da sua conta não corresponde ao e-mail convidado.";
  return err.detail ?? "Não foi possível aceitar o convite.";
}